<template>
	<div class="content-page content-page--environment-summary">
		<div class="content-page__heading" ref="contentPageHeading">
			<div class="content-page__breadcrumb-title">
				<h2 class="content-page__title" data-test="title">
					Zone Manager
				</h2>
			</div>
			<div class="content-page__actions">
				<Button
					className="btn--has-icon"
					iconClassName="icon-date-time"
					:iconBeforeLabel="true"
					labelText="View Schedule Dashboard"
					:btnClick="
						() => {
							viewScheduleDashboard();
						}
					"
				/>
			</div>
		</div>

		<div class="environment-summary__text">
			Click on the checkbox to select a card. The selected clients will be
			able to collectively move to a different zone.
		</div>

		<div
			class="results-toolbar results-toolbar--variant-seven"
			ref="resultsToolBar"
		>
			<Search
				class="results-toolbar__search"
				variant="toggle-icons"
				placeholder="Search by client name or number"
				v-model.lazy="filters.search"
				:width="'300px'"
			/>
			<DropdownSort
				class="results-toolbar__sort"
				:options="ddsOptions"
				:showClearSelectedButton="false"
				v-model="ddsValue"
			/>

			<div class="results-toolbar__filter">
				<Dropdown
					:showClearSelectedButton="true"
					:options="environmentsFilterOptions"
					v-model="filters.environmentId"
					:defaultValue="'All clients'"
					@change="selectedCustomers.length = 0"
					:zone="true"
				/>
			</div>

			<div class="results-toolbar__group">
				<span
					class="results-toolbar__group-move"
					@mouseover="tooltipMoveToVisible = true"
					@mouseleave="tooltipMoveToVisible = false"
					ref="menuButtonV2Ref"
				>
					<MenuButtonV2
						:options="environmentsMoveOptions"
						placeholder="Move Now"
						@selected="changeCxToZone"
						:disabled="!selectedCustomers.length"
						:width="'130px'"
					/>
					<Tooltip
						:text="moveToMessage"
						:display="displayMoveTooltip"
						:trackedRefName="'menuButtonV2Ref'"
						position="top"
						:offset="[0, 6]"
					/>
				</span>

				<span class="results-toolbar__group-text">or</span>

				<span
					class="results-toolbar__group-create"
					v-if="!isScheduleCreated"
					@mouseover="tooltipScheduleVisible = true"
					@mouseleave="tooltipScheduleVisible = false"
					ref="createScheduleButtonRef"
				>
					<Button
						:disabled="isDisabledCreateScheduleButton"
						:labelText="placeHolderScheduleBtn"
						:btnClick="
							() => {
								viewCreateSchedule = true;
							}
						"
					/>
					<Tooltip
						:text="scheduledMessage"
						:display="displayScheduledTooltip"
						:trackedRefName="'createScheduleButtonRef'"
						position="top"
						:offset="[0, 6]"
					/>
				</span>

				<span
					class="results-toolbar__group-edit"
					v-if="isScheduleCreated"
				>
					<MenuButtonV2
						:options="scheduleOptions"
						:placeholder="placeHolderScheduleBtn"
						@selected="scheduleAction"
						:disabled="!selectedCustomers.length"
						:width="'180px'"
					/>
				</span>

				<span
					class="results-toolbar__group-schedule-clients"
					v-show="countCustomerScheduleLabel"
					>{{ countCustomerScheduleLabel }}</span
				>

				<ResultCounter
					class="results-toolbar__counter"
					:counter="resultCounter || 0"
					v-show="showResultCounter"
				/>
			</div>
		</div>
		<BlockNotifications
			title="Selected clients"
			message="A maximum of 20 clients will be allowed to be added to a schedule at any given time."
			:blockState="'error'"
			:blockVariant="'small'"
			ref="blockNotification"
			v-if="selectedCustomers.length > 20"
		/>

		<ScrollContainer
			v-if="hasResults"
			variant="infinity-scroll"
			:hasMoreItems="hasMoreItemsToLoad"
			@scrollEnd="handleScrollEnd"
		>
			<EnvironmentList
				v-model="selectedCustomers"
				:items="customersList"
				:resetSelectedCheckboxes="resetSelectedCheckboxes"
				@resetSelectedCheckboxes="
					val => (resetSelectedCheckboxes = val)
				"
			/>
		</ScrollContainer>
		<Modal
			v-show="bulkMoveAction"
			:display="true"
			@show="bulkMoveAction = false"
			:title="confirmationTitle"
			:type="'confirmation'"
			labelConfirmButton="Apply Changes"
			labelCancelButton="Cancel"
			ref="bulkConfirmationModalRef"
		>
			<template #modal-content>
				<div class="bulk-move-modal">
					<div class="bulk-move-modal__text">
						You will move
						<span class="bulk-move-modal__text-bold">{{
							verbiageDealerships
						}}</span>
						to
						<span class="bulk-move-modal__text-bold">
							{{ zoneSelected }}.
						</span>
					</div>
					<ul class="bulk-move-modal__list">
						<li
							class="bulk-move-modal__list-item"
							v-for="id in selectedCustomers"
							:key="id"
						>
							<div v-if="customerById(id)">
								<p>
									{{
										customerById(id).displayName ||
											customerById(id).dealershipGroup
									}}
								</p>
							</div>
						</li>
					</ul>
				</div>
			</template>
		</Modal>
		<Modal
			v-if="viewMovedCx && latestCxMovement.length"
			:display="true"
			@show="viewMovedCx = false"
			title="Dealerships Moved"
		>
			<template #modal-content>
				<div class="bulk-move-modal">
					<div class="bulk-move-modal__text">
						The following items were moved to
						<span class="bulk-move-modal__text-bold">{{
							latestCxMovement[0].newZoneName
						}}</span>
					</div>
					<ul class="bulk-move-modal__list">
						<li
							class="bulk-move-modal__list-item"
							v-for="(name, index) in latestCxNamesMovement"
							:key="index"
						>
							<p>
								{{ name }}
							</p>
						</li>
					</ul>
				</div>
			</template>
		</Modal>
		<Spinner
			v-if="loading"
			:processor="{ loading: true }"
			data-test="spinner"
		/>
		<NoResults v-if="!loading && !hasResults" data-test="noResults" />
		<portal to="toastPortalDestination">
			<ToastV2 ref="simpleToastRef" className="toast-v2--success" useIcon>
				<span class="environment-summary__toast-text">
					{{ toastSuccefullMessage }}
				</span>
				<Button
					:btnClick="() => (viewMovedCx = true)"
					labelText="View dealerships"
					className="btn--inline-link"
				/>
			</ToastV2>
		</portal>
		<CreateScheduledModal
			:customers="selectedCustomers"
			v-model="viewCreateSchedule"
			@reload="reloadEnvironmentsPage"
		></CreateScheduledModal>
		<EnvironmentScheduleStopModal
			:schedule="currentScheduleById"
			:isStopSchedule="false"
			:titleModal="'The schedule will be stopped.'"
			v-model="viewStopSchedule"
			@reload="reloadEnvironmentsPage"
			@showAlert="val => (canShowAlert = val)"
		/>
		<Alert
			variant="alert--neutral"
			:title="'SCHEDULE NOTICE'"
			:display="showStopAlert"
			:message="showAlertMessage"
			:timerLength="10000"
		></Alert>
	</div>
</template>

<script>
import Dropdown from '@/components/forms/dropdown/dropdown.vue';
import MenuButtonV2 from '@/components/forms/button/menu-button-v2';
import ResultCounter from '@/site-components/result-counter/result-counter';
import Search from '@/components/forms/search/search';
import EnvironmentList from '@/sections/environment/summary/environment-summary-list.vue';
import NoResults from '@/site-components/no-results/no-results';
import Spinner from '@/components/visual/spinner/spinner';
import ScrollContainer from '@/components/shells/scroll-container/scroll-container';
import ToastV2 from '@/components/notifications/toast/toast-v2';
import Button from '@/components/forms/button/button';
import Modal from '@/components/shells/modal/modals';
import DropdownSort from '@/components/forms/dropdown/dropdown-sort';
import Tooltip from '@/components/shells/tooltip/tooltip';
import CreateScheduledModal from '@/sections/environment/schedule/create-edit/environment-schedule-create-modal.vue';
import EnvironmentScheduleStopModal from '@/sections/environment/schedule/shared/environment-schedule-stop-modal.vue';
import Alert from '@/components/notifications/alert/alert.vue';
import BlockNotifications from '@/components/notifications/block-notifications/block-notifications';
import _ from 'lodash';
import {
	MODULE_NAME as MODULE_OPTION_NAME,
	SET_ENVIRONMENTS,
	ENVIRONMENTS
} from '@/sections/options/options-mutation-type.js';

// mixins
//;
import { LoggingMixin } from 'vue-ui-layout';

import {
	removeObjectEmptyValues,
	removePropertiesWithSameValues
} from '@/utils/object';

import {
	Action,
	PlaceHoldersBtn,
	ActionMessages,
	FilterOptions
} from '@/sections/environment/schedule/shared/Const.js';

//vuex
import { mapActions, mapState, mapGetters } from 'vuex';

export default {
	name: 'EnvironmentSummaryPage',
	mixins: [LoggingMixin],
	components: {
		Dropdown,
		MenuButtonV2,
		ResultCounter,
		Search,
		EnvironmentList,
		NoResults,
		ScrollContainer,
		Spinner,
		Modal,
		ToastV2,
		Button,
		DropdownSort,
		Tooltip,
		CreateScheduledModal,
		EnvironmentScheduleStopModal,
		Alert,
		BlockNotifications
	},
	props: {
		search: {
			type: String
		},
		environmentId: {
			type: String
		},
		sortBy: {
			type: String
		},
		sortDirection: {
			type: String
		}
	},
	data() {
		return {
			environments: [],
			filters: {
				searchType: 'customer',
				search: this.search,
				sortby: this.sortBy,
				sortdirection: this.sortDirection,
				environmentId: this.environmentId || null
			},
			page: 1,
			countPerPage: this.count,
			defaultFilters: {
				search: '',
				searchType: 'customer',
				environmentId: null,
				sortby: 'name',
				sortdirection: 'asc'
			},
			ddsOptions: [
				{ text: 'Name', value: 'name' },
				{ text: 'Number', value: 'number' },
				{ text: 'Last Moved', value: 'environmentActiveOn' }
			],
			loading: true,
			loadingMore: false,
			selectedCustomers: [],
			zoneSelected: '',
			bulkMoveAction: false,
			viewMovedCx: false,
			latestCxMovement: [],
			latestCxNamesMovement: [],
			toastSuccefullMessage: '',
			tooltipMoveToVisible: false,
			tooltipScheduleVisible: false,
			viewCreateSchedule: false,
			moveToMessage: 'Select at least one client to enable moving zones',
			scheduledMessage: 'Select at least one client to create a schedule',
			viewStopSchedule: false,
			ddsActionsOptions: [
				{
					text: 'Create a scheduled move',
					value: Action.create
				},
				{
					text: 'Add client to schedule',
					value: Action.add
				},
				{
					text: 'Remove client from schedule',
					value: Action.remove
				}
			],
			ID_SCHEDULED: '-1',
			canShowAlert: false,
			currentScheduleById: {},
			resetSelectedCheckboxes: false
		};
	},
	mounted() {
		this.getApiEnvironments();
	},
	computed: {
		...mapState('customers', ['customers']),
		...mapGetters(MODULE_OPTION_NAME, [ENVIRONMENTS]),
		...mapGetters('customers', ['customerById']),
		ddsValue: {
			get() {
				return {
					sortby: this.filters.sortby,
					sortdirection: this.filters.sortdirection
				};
			},
			set(value) {
				this.filters.sortby = value.sortby;
				this.filters.sortdirection = value.sortdirection;
			}
		},
		showStopAlert() {
			return (
				this.canShowAlert &&
				!_.isEmpty(this.currentScheduleById.scheduledDate)
			);
		},
		showAlertMessage() {
			return `Scheduled move for ${this.currentScheduleById.scheduledDate} has been stopped`;
		},
		showResultCounter() {
			if (this.loading) {
				return false;
			}
			return true;
		},
		hasResults() {
			return this.customers.items?.length && !this.loading;
		},
		resultCounter() {
			const totalItems = _.get(this.customers, 'items.length', 0);
			const totalCount = _.get(this.customers, 'totalCount', 0);
			return `${totalItems} of ${totalCount}`;
		},
		hasMoreItemsToLoad() {
			return _.isEmpty(this.customers)
				? false
				: this.page < this.customers.totalPageCount;
		},
		customersList() {
			return this.customers.items || [];
		},
		countSelectedCustomers() {
			return this.selectedCustomers.length ?? 0;
		},
		verbiageDealerships() {
			if (this.countSelectedCustomers > 1) {
				return `${this.countSelectedCustomers} dealerships`;
			}
			return `${this.countSelectedCustomers} dealership`;
		},
		confirmationTitle() {
			if (this.countSelectedCustomers > 1) {
				return 'Bulk Move Action';
			}
			return 'Single Move Confirmation';
		},
		displayMoveTooltip() {
			return this.tooltipMoveToVisible && !this.selectedCustomers.length;
		},
		displayScheduledTooltip() {
			return (
				this.tooltipScheduleVisible && !this.selectedCustomers.length
			);
		},
		countCustomerScheduleLabel() {
			if (this.loading) {
				return '';
			}
			const label =
				this.scheduledClientCount > 1
					? 'Scheduled client'
					: 'Scheduled clients';
			return `${label}: ${this.scheduledClientCount}`;
		},
		isAllScheduleClientsChecked() {
			if (
				!_.isEmpty(this.customersList) &&
				!_.isEmpty(this.selectedObjCustomers)
			) {
				const scheduledTrueCustomerIds = this.customersList
					.filter(customer => customer.scheduled === true)
					.map(customer => customer.id);

				const allScheduledCustomersSelected = scheduledTrueCustomerIds.every(
					customerId =>
						this.selectedObjCustomers.some(
							selectedCustomer =>
								selectedCustomer.id === customerId
						)
				);
				return allScheduledCustomersSelected;
			}

			return false;
		},
		environmentsMoveOptions() {
			return this.environments;
		},
		environmentsFilterOptions() {
			const filters = [
				{ text: FilterOptions.allClients, value: null },
				{
					text: FilterOptions.scheduleClients,
					value: this.ID_SCHEDULED
				}
			];

			return [...filters, ...this.environments];
		},
		scheduleOptions() {
			const customersScheduledMapped = this.selectedObjCustomers.map(
				obj => obj.scheduled
			);
			const allTrue = customersScheduledMapped.every(val => val === true);
			const allFalse = customersScheduledMapped.every(
				val => val === false
			);
			const customersToBeAdd = this.selectedObjCustomers.filter(
				customer => customer.scheduled == false
			);

			const maxSelectedScheduledAllow = 20 - this.scheduledClientCount;

			if (this.isScheduleCreated) {
				let actionValueToDisable =
					allTrue ||
					customersToBeAdd.length > maxSelectedScheduledAllow
						? Action.add
						: allFalse
						? Action.remove
						: null;

				return this.ddsActionsOptions
					.filter(action => action.value !== Action.create)
					.map(option => {
						const isAddAction = option.value === 'add';
						const isRemoveAction = option.value === 'remove';
						const shouldDisable =
							option.value === actionValueToDisable
								? true
								: isAddAction
								? !this.hasUpdatePermission
								: isRemoveAction
								? !this.hasRemovePermission
								: true;
						return {
							...option,
							disabled: shouldDisable
						};
					});
			} else {
				return this.ddsActionsOptions.filter(
					action => action.value === Action.create
				);
			}
		},
		placeHolderScheduleBtn() {
			return this.isScheduleCreated
				? PlaceHoldersBtn.edit
				: PlaceHoldersBtn.create;
		},
		isScheduleCreated() {
			return this.scheduledClientCount > 0;
		},
		selectedObjCustomers() {
			if (
				_.isEmpty(this.customers.items) ||
				_.isEmpty(this.selectedCustomers)
			) {
				return [];
			}
			return this.customers.items
				.filter(item => this.selectedCustomers.includes(item.id))
				.map(({ id, scheduled }) => ({ id, scheduled }));
		},
		scheduledClientCount() {
			return _.get(this.customers, 'totalScheduled', 0);
		},
		isDisabledCreateScheduleButton() {
			return (
				!this.selectedCustomers.length ||
				this.selectedCustomers.length > 20 ||
				!this.hasCreatePermission
			);
		},
		hasCreatePermission() {
			return this.validatePermission('environments.create');
		},
		hasUpdatePermission() {
			return this.validatePermission('environments.update');
		},
		hasRemovePermission() {
			return this.validatePermission('environments.delete');
		}
	},
	methods: {
		...mapActions('customers', ['loadCustomers', 'moveToZone']),
		...mapActions(MODULE_OPTION_NAME, {
			getEnvironments: SET_ENVIRONMENTS
		}),
		...mapActions('environments', {
			updateCustomerScheduleAction: 'updateCustomerSchedule',
			getScheduleByIdAction: 'getSecheduleById'
		}),
		async getCurrentScheduleById() {
			try {
				const id = this.customers.scheduleId;
				if (this.customers.scheduleId) {
					await this.getScheduleByIdAction(id).then(response => {
						this.currentScheduleById = {
							...response,
							actionComment: ''
						};

						this.viewStopSchedule = true;
					});
				}
			} catch (error) {
				console.log(error);
			}
		},
		async getApiEnvironments() {
			if (_.isEmpty(this[ENVIRONMENTS])) {
				await this.getEnvironments({ active: true }).then(response => {
					this.environments = response.items.map(x => {
						return {
							text: x.description,
							value: x.value
						};
					});
				});
			} else {
				this.environments = this[ENVIRONMENTS].map(x => {
					return {
						text: x.description,
						value: x.value
					};
				});
			}
			this.environments.sort((a, b) => {
				return a.value - b.value;
			});
		},
		async fetchData(page) {
			if (!this.loadingMore) {
				this.loading = true;
			}

			let query = removeObjectEmptyValues(this.filters);

			query.page = page;
			query.count = this.countPerPage || 100;

			try {
				await this.loadCustomers(query);
			} finally {
				this.loading = false;
				this.loadingMore = false;
			}
		},
		async updateCurrentSchedule(payload) {
			let status = 200;
			let isSuccessful = true;
			try {
				await this.updateCustomerScheduleAction(payload);
			} catch (error) {
				status = 400;
				isSuccessful = false;
				console.log(error);
			}
			this.actionScheduleMessage({
				action: payload.action,
				status,
				isSuccessful,
				customers: payload.customers
			});
			this.reloadEnvironmentsPage(true);
		},
		scheduleAction(action) {
			const customers = this.getSelectedCustomersMapByAction(action);
			const payload = { customers, action };

			if (action === Action.create) {
				this.viewCreateSchedule = true;
				return;
			}

			if (action === Action.add) {
				this.updateCurrentSchedule(payload);
				return;
			}

			if (action === Action.remove) {
				if (this.isAllScheduleClientsChecked) {
					this.getCurrentScheduleById();
				} else {
					this.updateCurrentSchedule(payload);
				}
				return;
			}
		},
		getSelectedCustomersMapByAction(action) {
			const filterCondition =
				action === Action.add
					? customer => !customer.scheduled
					: customer => customer.scheduled;

			const customers = this.selectedObjCustomers
				.filter(filterCondition)
				.map(customer => ({ customerId: customer.id }));

			return customers;
		},
		actionScheduleMessage(value) {
			const customMessage = value.isSuccessful
				? ActionMessages[value.action].success
				: ActionMessages[value.action].failed;

			const countCustomers = value.customers.length;

			const customerMessage =
				countCustomers > 1
					? `clients were ${customMessage}`
					: `client was ${customMessage}`;

			const customerNames = value.customers.map(
				x => this.customerById(x.customerId).displayName
			);

			this.$store.dispatch('toastMessages/show', {
				statusCode: value.status,
				message: `${countCustomers} ${customerMessage} ${customerNames}`,
				slot: 'default',
				useIcon: true,
				userDismissable: true
			});
		},
		updateRoute() {
			let queries = removePropertiesWithSameValues(
				this.filters,
				this.defaultFilters
			);

			queries = removeObjectEmptyValues(queries);
			if (this.countPerPage) {
				queries.count = this.countPerPage;
			}

			this.$router
				.push({
					path: '/environments',
					query: queries
				})
				.catch(() => {});
		},
		handleScrollEnd() {
			if (this.hasMoreItemsToLoad) {
				this.loadingMore = true;
				this.page += 1;
			}
		},
		async changeCxToZone(zoneId) {
			this.zoneSelected = this.environmentsMoveOptions.find(
				zone => zone.value == zoneId
			).text;

			this.bulkMoveAction = true;

			const modalResponse = await this.$refs.bulkConfirmationModalRef?.confirm();
			if (!modalResponse) return;

			const updates = [];
			this.latestCxNamesMovement = [];

			for (let i = 0; i < this.selectedCustomers.length; i++) {
				const cx = this.customerById(this.selectedCustomers[i]);

				if (cx.environmentId == zoneId) continue;

				this.latestCxNamesMovement.push(
					cx.displayName || cx.dealershipGroup
				);

				updates.push({
					customerId: this.selectedCustomers[i],
					NewZoneId: zoneId
				});
			}

			if (!updates.length) {
				this.selectedCustomers = [];
				this.resetSelectedCheckboxes = true;
				return;
			}

			this.loading = true;

			try {
				this.latestCxMovement = await this.moveToZone(updates);

				this.toastSuccefullMessage = `Success! ${
					this.latestCxMovement.length
				} dealership(s) moved to ${this.latestCxMovement[0]
					.newZoneName || 'a New Zone'}`;

				this.$nextTick(() => {
					this.$nextTick(() => {
						if (this.$refs.simpleToastRef) {
							this.$refs.simpleToastRef.show();
						}
					});
				});
			} finally {
				this.loading = false;
				this.selectedCustomers = [];
				this.fetchData(1);
			}
		},
		reloadEnvironmentsPage(val) {
			if (val) {
				this.selectedCustomers = [];
				this.filters.environmentId = null;
				this.fetchData(1);
			}
		},
		viewScheduleDashboard() {
			this.$router.push({
				path: `/environments/schedule`
			});
		}
	},
	watch: {
		filters: {
			deep: true,
			immediate: true,
			handler(newValue) {
				this.updateRoute(newValue);
				if (this.page != 1) {
					this.page = 1;
				} else {
					this.fetchData(1);
				}
				this.selectedCustomers = [];
			}
		},
		page: {
			handler(page) {
				this.fetchData(page);
			}
		},
		'$route.query': {
			immediate: true,
			handler(newQuery) {
				let newfilters = { ...this.defaultFilters, ...newQuery };
				if (!_.isEqual(newfilters, this.filters)) {
					this.filters = _.cloneDeep(newfilters);
				}
			}
		}
	},
	beforeRouteLeave(to, from, next) {
		const breadcrumbs = [
			{
				name: 'Zone Manager',
				data: { path: from.fullPath }
			}
		];
		if (to.name === 'environment-history') {
			to.meta.breadcrumb = [...breadcrumbs, { name: 'History' }];
		}
		if (to.name === 'environment-schedule-dashboard') {
			to.meta.breadcrumb = [...breadcrumbs, { name: 'Dashboard' }];
		}
		const obj = { path: to.fullPath, breadcrumb: to.meta.breadcrumb };
		this.$store.commit('setBreadCrumbs', obj);
		next();
	}
};
</script>

<style lang="scss" scoped>
/**
	* Environment Page
	* Can use SCSS variables = TRUE
    * This should only contain classes and styles used for the page layout.
*/

::v-deep .modal {
	width: 520px;
}

::v-deep .toast-v2 {
	max-width: 540px;
}

::v-deep .tooltip {
	z-index: 2500;
}

.results-toolbar {
	&__filter {
		& * {
			min-width: 160px;
		}
	}

	&__group {
		width: 100%;
		display: flex;
		gap: $gutter-md;
		align-items: center;

		@include body-text('regular');
	}

	&__group-create,
	&__group-schedule-clients {
		min-width: fit-content;
	}

	&__group-schedule-clients {
		align-self: self-end;
		color: $neutral-2;
	}
}

.environment-summary {
	&__text {
		@include sub-heading;
		margin-bottom: $gutter-lg;
	}

	&__toast-text {
		@include body-text('regular');
		margin-right: $gutter-xl;
	}
}

.bulk-move-modal {
	@include body-text('regular');

	&__text {
		&-bold {
			@include body-text('regular-emphasis');
		}
	}

	&__list {
		overflow-y: auto;
		margin-top: $gutter-xl;
		max-height: 160px;
		min-height: 24px;
		@include scrollbar;

		&-item {
			&:not(:first-child) {
				margin-top: $gutter-md;
			}
		}
	}
}
</style>
